import { LoaderFunction, useLoaderData } from "react-router-dom";

import { get } from "api";
import { Models } from "api/types";
import {
  RetailProductEarnedSection,
  RetailProductPastOrdersSection,
  RetailProductUpcomingOrdersSection,
} from "modules/retailProducts";
import { NoTreatmentsContainer } from "organisms/treatment-list/TreatmentList";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

const load = async () => {
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const platformLocations = queryClient.fetchQuery(
    ["/platform/locations"],
    () => get("/platform/locations", { page: 1 })
  );

  const areRetailProductsVisible = queryClient.fetchQuery(
    ["/retailproducts/are-retail-products-visible"],
    () => get("/retailproducts/are-retail-products-visible"),
    { staleTime: 1000 * 10 }
  );

  const productsOrders = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/orders"],
    async () => {
      const platformData = await platform;
      return await get("/retailproducts/{platformId}/orders", {
        platformId: platformData.id,
      });
    }
  );

  const retailProductRefunds = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/refund-support-details"],
    async () => {
      const platformData = await platform;
      const ordersData = await productsOrders;

      if (ordersData.length === 0) {
        return [];
      }

      const refundPromises = ordersData.map(async (order) => {
        try {
          const refundDetails = await get(
            "/retailproducts/{platformId}/refund-support-details/{orderId}",
            {
              platformId: platformData.id,
              orderId: order.orderID,
            }
          );
          return refundDetails;
        } catch (error) {
          console.warn(
            `No refund details found for order ${order.orderID}:`,
            error
          );
          return null;
        }
      });
      const refundResults = await Promise.all(refundPromises);

      return refundResults
        .filter((result) => result !== null)
        .map((result) => result);
    }
  );

  const earnedProducts = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/earned"],
    async () => {
      const platformData = await platform;
      return await get("/retailproducts/{platformId}/earned", {
        platformId: platformData.id,
      });
    }
  );

  return waitObj({
    platform,
    platformLocations,
    areRetailProductsVisible,
    productsOrders,
    retailProductRefunds,
    earnedProducts,
  });
};

export type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletProductsLoader: LoaderFunction = async () => {
  return await load();
};

const WalletProductsPage = () => {
  const {
    productsOrders,
    platformLocations,
    retailProductRefunds,
    earnedProducts,
  } = useLoaderData() as LoaderData;

  if (productsOrders.length === 0 && earnedProducts.length === 0) {
    return (
      <div className="mx-4 mt-4 overflow-clip rounded-lg">
        <NoTreatmentsContainer text="You haven't purchased any products yet. Browse the shop to buy your first product and start your journey!" />
      </div>
    );
  }

  const locations =
    (platformLocations.items as Models<"PlatformLocationModel1">[]) || [];

  return (
    <div className="flex flex-col">
      {earnedProducts.length > 0 && (
        <div className="px-4 pb-6 pt-4">
          <RetailProductEarnedSection
            earnedProducts={earnedProducts}
            platformLocations={locations}
          />
        </div>
      )}
      {productsOrders.length > 0 && (
        <div className="px-4 pb-6 pt-4">
          <RetailProductUpcomingOrdersSection
            productsOrders={productsOrders}
            platformLocations={locations}
          />
        </div>
      )}
      <div className="px-4 pb-6">
        <RetailProductPastOrdersSection
          productsOrders={productsOrders}
          retailProductRefunds={retailProductRefunds}
          earnedProducts={earnedProducts}
        />
      </div>
    </div>
  );
};

export default WalletProductsPage;
